import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './general-health.reducer';

export const GeneralHealth = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const generalHealthList = useAppSelector(state => state.generalHealth.entities);
  const loading = useAppSelector(state => state.generalHealth.loading);
  const totalItems = useAppSelector(state => state.generalHealth.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="general-health-heading" data-cy="GeneralHealthHeading">
        <Translate contentKey="apiLocationApp.generalHealth.home.title">General Healths</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.generalHealth.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/general-health/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.generalHealth.home.createLabel">Create new General Health</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {generalHealthList && generalHealthList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.generalHealth.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.generalHealth.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.generalHealth.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.generalHealth.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.generalHealth.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.generalHealth.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('veryGoodHealth')}>
                  <Translate contentKey="apiLocationApp.generalHealth.veryGoodHealth">Very Good Health</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('veryGoodHealth')} />
                </th>
                <th className="hand" onClick={sort('goodHealth')}>
                  <Translate contentKey="apiLocationApp.generalHealth.goodHealth">Good Health</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('goodHealth')} />
                </th>
                <th className="hand" onClick={sort('fairHealth')}>
                  <Translate contentKey="apiLocationApp.generalHealth.fairHealth">Fair Health</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fairHealth')} />
                </th>
                <th className="hand" onClick={sort('badHealth')}>
                  <Translate contentKey="apiLocationApp.generalHealth.badHealth">Bad Health</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('badHealth')} />
                </th>
                <th className="hand" onClick={sort('veryBadHealth')}>
                  <Translate contentKey="apiLocationApp.generalHealth.veryBadHealth">Very Bad Health</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('veryBadHealth')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {generalHealthList.map((generalHealth, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/general-health/${generalHealth.id}`} color="link" size="sm">
                      {generalHealth.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${generalHealth.geographicLevel}`} />
                  </td>
                  <td>{generalHealth.date}</td>
                  <td>{generalHealth.geography}</td>
                  <td>{generalHealth.geographyCode}</td>
                  <td>{generalHealth.totalAllUsualResidents}</td>
                  <td>{generalHealth.veryGoodHealth}</td>
                  <td>{generalHealth.goodHealth}</td>
                  <td>{generalHealth.fairHealth}</td>
                  <td>{generalHealth.badHealth}</td>
                  <td>{generalHealth.veryBadHealth}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/general-health/${generalHealth.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/general-health/${generalHealth.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/general-health/${generalHealth.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.generalHealth.home.notFound">No General Healths found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={generalHealthList && generalHealthList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default GeneralHealth;
